import { computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';

export const DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER = Object.freeze([
  { name: 'conversation_actions' },
  { name: 'macros' },
  { name: 'conversation_info' },
  { name: 'contact_attributes' },
  { name: 'contact_notes' },
  { name: 'internal_tasks' },
  { name: 'shared_files' },
  { name: 'previous_conversation' },
  { name: 'conversation_participants' },
  { name: 'linear_issues' },
  { name: 'calendar_events' },
  { name: 'shopify_orders' },
]);

export const DEFAULT_CONVERSATION_SIDEBAR_VISIBLE_ITEMS = Object.freeze(
  DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER.map(item => item.name)
);

export const DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER = Object.freeze([
  { name: 'contact_attributes' },
  { name: 'contact_labels' },
  { name: 'previous_conversation' },
]);

/** Sidebar section name â†’ custom attribute model for category prefs */
export const SIDEBAR_SECTION_ATTRIBUTE_TYPE = Object.freeze({
  conversation_info: 'conversation_attribute',
  contact_attributes: 'contact_attribute',
});

export const UNCATEGORIZED_CATEGORY_SLUG = 'uncategorized';

/** Built-in category for conversation metadata (browser, IP, referer, â€¦) */
export const SYSTEM_CATEGORY_SLUG = 'system';

/**
 * Slugify a custom-attribute category label (shared by sidebar menu + CustomAttributes).
 * @param {string} category
 * @returns {string}
 */
export const attributeCategorySlug = category => {
  const trimmed = (category || '').trim();
  if (!trimmed) return UNCATEGORIZED_CATEGORY_SLUG;
  return trimmed
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '');
};

/**
 * Slugifies the channel name.
 * Replaces spaces, hyphens, and double colons with underscores.
 * @param {string} name - The channel name to slugify.
 * @returns {string} The slugified channel name.
 */
const slugifyChannel = name =>
  name?.toLowerCase().replace(' ', '_').replace('-', '_').replace('::', '_');

/**
 * Computes the order of items in the conversation sidebar, using defaults if not present.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {Array} Ordered list of sidebar items.
 */
const useConversationSidebarItemsOrder = uiSettings => {
  return computed(() => {
    const { conversation_sidebar_items_order: itemsOrder } = uiSettings.value;
    // If the sidebar order is not set, use the default order.
    if (!itemsOrder) {
      return [...DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER];
    }
    // Create a copy of itemsOrder to avoid mutating the original store object.
    const itemsOrderCopy = [...itemsOrder];
    // If the sidebar order doesn't have the new elements, then add them to the list.
    DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER.forEach(item => {
      if (!itemsOrderCopy.find(i => i.name === item.name)) {
        itemsOrderCopy.push(item);
      }
    });
    return itemsOrderCopy;
  });
};

/**
 * Computes the order of items in the contact sidebar,using defaults if not present.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {Array} Ordered list of sidebar items.
 */
const useContactSidebarItemsOrder = uiSettings => {
  return computed(() => {
    const { contact_sidebar_items_order: itemsOrder } = uiSettings.value;
    return itemsOrder || DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER;
  });
};

/**
 * Toggles the open state of a sidebar item.
 * @param {string} key - The key of the sidebar item to toggle.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const toggleSidebarUIState = (key, uiSettings, updateUISettings) => {
  updateUISettings({ [key]: !uiSettings.value[key] });
};

/**
 * Computes the visible items in the conversation sidebar, using defaults if not present.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {Array} List of visible sidebar item names.
 */
const useConversationSidebarVisibleItems = uiSettings => {
  return computed(() => {
    const { conversation_sidebar_visible_items: visibleItems } =
      uiSettings.value;
    if (!visibleItems) {
      return [...DEFAULT_CONVERSATION_SIDEBAR_VISIBLE_ITEMS];
    }
    return visibleItems;
  });
};

/**
 * Toggles the visibility of a conversation sidebar item.
 * @param {string} name - Item name to toggle.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const toggleConversationSidebarItemVisibility = (
  name,
  uiSettings,
  updateUISettings
) => {
  const visible = new Set(
    uiSettings.value.conversation_sidebar_visible_items ||
      DEFAULT_CONVERSATION_SIDEBAR_VISIBLE_ITEMS
  );
  if (visible.has(name)) {
    visible.delete(name);
  } else {
    visible.add(name);
  }
  updateUISettings({
    conversation_sidebar_visible_items: [...visible],
  });
};

/**
 * Resets conversation sidebar visibility, section order, and category prefs.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const resetConversationSidebarVisibility = updateUISettings => {
  updateUISettings({
    conversation_sidebar_visible_items: [
      ...DEFAULT_CONVERSATION_SIDEBAR_VISIBLE_ITEMS,
    ],
    conversation_sidebar_items_order: [
      ...DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER,
    ],
    conversation_sidebar_visible_categories: {},
    conversation_sidebar_category_order: {},
    conversation_sidebar_menu_collapsed_sections: [],
  });
};

/**
 * Whether a category slug is visible for an attribute type.
 * Missing key = all visible; explicit array (incl. empty) = that list.
 * @param {string} attributeType
 * @param {string} slug
 * @param {Object} uiSettings
 * @returns {boolean}
 */
const isConversationSidebarCategoryVisible = (
  attributeType,
  slug,
  uiSettings
) => {
  const byType = uiSettings.value.conversation_sidebar_visible_categories;
  if (!byType || !(attributeType in byType) || byType[attributeType] == null) {
    return true;
  }
  return byType[attributeType].includes(slug);
};

/**
 * Toggle category visibility for an attribute type.
 * @param {string} attributeType
 * @param {string} slug
 * @param {string[]} allSlugs - Known category slugs (seed when unset)
 * @param {Object} uiSettings
 * @param {Function} updateUISettings
 */
const toggleConversationSidebarCategoryVisibility = (
  attributeType,
  slug,
  allSlugs,
  uiSettings,
  updateUISettings
) => {
  const byType = uiSettings.value.conversation_sidebar_visible_categories || {};
  const current = byType[attributeType];
  const visible = new Set(current == null ? allSlugs : current);
  if (visible.has(slug)) {
    visible.delete(slug);
  } else {
    visible.add(slug);
  }
  updateUISettings({
    conversation_sidebar_visible_categories: {
      ...byType,
      [attributeType]: [...visible],
    },
  });
};

/**
 * Persist category folder order for an attribute type.
 * @param {string} attributeType
 * @param {string[]} order - Ordered category slugs
 * @param {Object} uiSettings
 * @param {Function} updateUISettings
 */
const reorderConversationSidebarCategories = (
  attributeType,
  order,
  uiSettings,
  updateUISettings
) => {
  updateUISettings({
    conversation_sidebar_category_order: {
      ...(uiSettings.value.conversation_sidebar_category_order || {}),
      [attributeType]: order,
    },
  });
};

/**
 * Ordered category slugs for an attribute type (may be empty = use default sort).
 * @param {string} attributeType
 * @param {Object} uiSettings
 * @returns {string[]}
 */
const conversationSidebarCategoryOrder = (attributeType, uiSettings) => {
  return (
    uiSettings.value.conversation_sidebar_category_order?.[attributeType] || []
  );
};

/**
 * Sets the signature flag for a specific channel type in the inbox settings.
 * @param {string} channelType - The type of the channel.
 * @param {boolean} value - The value to set for the signature enabled flag.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const setSignatureFlagForInbox = (channelType, value, updateUISettings) => {
  if (!channelType) return;

  const slugifiedChannel = slugifyChannel(channelType);
  updateUISettings({ [`${slugifiedChannel}_signature_enabled`]: value });
};

const setQuotedReplyFlagForInbox = (channelType, value, updateUISettings) => {
  if (!channelType) return;

  const slugifiedChannel = slugifyChannel(channelType);
  updateUISettings({ [`${slugifiedChannel}_quoted_reply_enabled`]: value });
};

/**
 * Fetches the signature flag for a specific channel type from UI settings.
 * @param {string} channelType - The type of the channel.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {boolean} The value of the signature enabled flag.
 */
const fetchSignatureFlagFromUISettings = (channelType, uiSettings) => {
  if (!channelType) return false;

  const slugifiedChannel = slugifyChannel(channelType);
  return uiSettings.value[`${slugifiedChannel}_signature_enabled`];
};

const fetchQuotedReplyFlagFromUISettings = (channelType, uiSettings) => {
  if (!channelType) return false;

  const slugifiedChannel = slugifyChannel(channelType);
  return uiSettings.value[`${slugifiedChannel}_quoted_reply_enabled`];
};

/**
 * Checks if a specific editor hotkey is enabled.
 * @param {string} key - The key to check.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {boolean} True if the hotkey is enabled, otherwise false.
 */
const isEditorHotKeyEnabled = (key, uiSettings) => {
  const {
    editor_message_key: editorMessageKey,
    enter_to_send_enabled: enterToSendEnabled,
  } = uiSettings.value || {};
  if (!editorMessageKey) {
    return key === (enterToSendEnabled ? 'enter' : 'cmd_enter');
  }
  return editorMessageKey === key;
};

/**
 * Main composable function for managing UI settings.
 * @returns {Object} An object containing reactive properties and methods for UI settings management.
 */
export function useUISettings() {
  const getters = useStoreGetters();
  const store = useStore();
  const uiSettings = computed(() => getters.getUISettings.value);

  const updateUISettings = (settings = {}) => {
    store.dispatch('updateUISettings', {
      uiSettings: {
        ...uiSettings.value,
        ...settings,
      },
    });
  };

  const isOnExpandedLayout = computed(() => {
    const {
      LAYOUT_TYPES: { CONDENSED },
    } = wootConstants;
    const { conversation_display_type: conversationDisplayType = CONDENSED } =
      uiSettings.value;
    return conversationDisplayType !== CONDENSED;
  });

  return {
    uiSettings,
    updateUISettings,
    isOnExpandedLayout,
    conversationSidebarItemsOrder: useConversationSidebarItemsOrder(uiSettings),
    conversationSidebarVisibleItems:
      useConversationSidebarVisibleItems(uiSettings),
    contactSidebarItemsOrder: useContactSidebarItemsOrder(uiSettings),
    isContactSidebarItemOpen: key => !!uiSettings.value[key],
    toggleSidebarUIState: key =>
      toggleSidebarUIState(key, uiSettings, updateUISettings),
    isConversationSidebarItemVisible: name => {
      const visible =
        uiSettings.value.conversation_sidebar_visible_items ||
        DEFAULT_CONVERSATION_SIDEBAR_VISIBLE_ITEMS;
      return visible.includes(name);
    },
    toggleConversationSidebarItemVisibility: name =>
      toggleConversationSidebarItemVisibility(
        name,
        uiSettings,
        updateUISettings
      ),
    resetConversationSidebarVisibility: () =>
      resetConversationSidebarVisibility(updateUISettings),
    isConversationSidebarCategoryVisible: (attributeType, slug) =>
      isConversationSidebarCategoryVisible(attributeType, slug, uiSettings),
    toggleConversationSidebarCategoryVisibility: (
      attributeType,
      slug,
      allSlugs
    ) =>
      toggleConversationSidebarCategoryVisibility(
        attributeType,
        slug,
        allSlugs,
        uiSettings,
        updateUISettings
      ),
    reorderConversationSidebarCategories: (attributeType, order) =>
      reorderConversationSidebarCategories(
        attributeType,
        order,
        uiSettings,
        updateUISettings
      ),
    conversationSidebarCategoryOrder: attributeType =>
      conversationSidebarCategoryOrder(attributeType, uiSettings),
    setConversationSidebarItemsOrder: itemsOrder =>
      updateUISettings({ conversation_sidebar_items_order: itemsOrder }),
    setSignatureFlagForInbox: (channelType, value) =>
      setSignatureFlagForInbox(channelType, value, updateUISettings),
    fetchSignatureFlagFromUISettings: channelType =>
      fetchSignatureFlagFromUISettings(channelType, uiSettings),
    setQuotedReplyFlagForInbox: (channelType, value) =>
      setQuotedReplyFlagForInbox(channelType, value, updateUISettings),
    fetchQuotedReplyFlagFromUISettings: channelType =>
      fetchQuotedReplyFlagFromUISettings(channelType, uiSettings),
    isEditorHotKeyEnabled: key => isEditorHotKeyEnabled(key, uiSettings),
  };
}
